<template>
  <z-card>
    <z-title>
      Exercício de fixação
    </z-title>

    <div v-if="!finished">
      <z-stepper-bar
        class="z-mt-0200 z-mb-0400"
        :max="exercises.length"
        :value="currentExerciseIndex + 1"
      />

      <z-text weight="medium">
        {{ currentExercise.origin }}
      </z-text>

      <div
        class="lesson-exercises__question"
        v-html="currentExercise.question"
      />

      <div
        v-if="!selectedAnswer"
        class="lesson-exercises__options"
      >
        <z-card
          v-for="(answer, index) in currentExercise.answers"
          :key="index"
          class="lesson-exercises__option"
          @click.native="onAnswerClick(answer)"
        >
          <ui-badge-ordination
            class="z-mr-0200"
            :aria-label="`Opção ${answer.letter}`"
            :value="answer.letter"
          />

          <z-text>
            {{ answer.text }}
          </z-text>
        </z-card>
      </div>

      <div
        v-if="selectedAnswer"
        class="lesson-exercises__answers"
      >
        <div
          v-if="!selectedAnswer.right"
          class="lesson-exercises__answer lesson-exercises__answer--wrong"
        >
          <ui-badge-ordination
            variant="solid"
            class="lesson-exercises__answer-letter"
            :aria-label="`Opção selecionada letra ${selectedAnswer.letter}`"
            :value="selectedAnswer.letter"
          />

          <z-text class="lesson-exercises__answer-text">
            {{ selectedAnswer.text }}
          </z-text>

          <z-icon
            :icon="['fal', 'times']"
            color="danger"
            size="small"
          />
        </div>

        <z-title size="nano">
          Resolução
        </z-title>

        <div class="lesson-exercises__answer lesson-exercises__answer--correct">
          <ui-badge-ordination
            variant="solid"
            class="lesson-exercises__answer-letter"
            :aria-label="`Opção selecionada letra ${correctAnswer.letter}`"
            :value="correctAnswer.letter"
          />

          <z-text class="lesson-exercises__answer-text">
            {{ correctAnswer.text }}
          </z-text>

          <z-icon
            :icon="['fal', 'check']"
            color="success"
            size="small"
          />
        </div>

        <div
          class="lesson-exercises__explanation"
          v-html="currentExercise.explanation"
        />

        <z-button
          class="lesson-exercises__cta"
          @click="onNextClick"
        >
          Próxima pergunta
        </z-button>
      </div>
    </div>

    <div
      v-if="finished"
      class="lesson-exercises__finished"
    >
      <img
        class="lesson-exercises__illustration"
        :src="$asset(finishedData.illustration, { source: 'kong' })"
        :alt="finishedData.illustrationAlt"
      >

      <z-title size="small">
        {{ finishedData.title }}
      </z-title>

      <z-text>
        {{ finishedData.description }}
      </z-text>

      <z-button
        class="lesson-exercises__cta"
        @click="onResetClick"
      >
        Refazer o exercício
      </z-button>
    </div>
  </z-card>
</template>

<script>
import UiBadgeOrdination from '~/components/ui/ui-badge-ordination'

export default {
  name: 'LessonExercises',
  components: {
    UiBadgeOrdination,
  },
  props: {
    exercises: {
      type: Array,
      required: true,
    },
  },
  data () {
    return {
      currentExerciseIndex: 0,
      correctAnswersCount: 0,
      selectedAnswer: null,
      finished: false,
    }
  },
  computed: {
    currentExercise () {
      return this.exercises[this.currentExerciseIndex]
    },
    correctAnswer () {
      return this.currentExercise.answers.filter(answer => answer.right)[0]
    },
    finishedData () {
      if (this.correctAnswersCount === this.exercises.length) {
        return {
          title: 'Boa! Você acertou todas as questões',
          description: 'Você mostrou que entendeu a matéria e mandou bem no teste. Siga nesse ritmo e você irá super bem nas provas. Boa sorte!',
          illustration: '/il03oc02lb_001.svg',
          illustrationAlt: 'Ilustração de uma pessoa caminhando segurando um celular na mão',
        }
      }

      return {
        title: `Hummm… Você acertou ${this.correctAnswersCount} de ${this.exercises.length} questões`,
        description: 'Ainda falta dá uma melhorada, mas uma revisão no conteúdo pode ajudar! Que tal estudar a matéria novamente e refazer os exercícios?',
        illustration: '/il03oc02ld_001.svg',
        illustrationAlt: 'Ilustração de uma pessoa sentada em uma cadeira utilizando um notebook',
      }
    },
  },
  updated () {
    MathJax.Hub.Typeset()
  },
  methods: {
    onAnswerClick (answer) {
      this.selectedAnswer = answer

      if (answer.right) {
        this.correctAnswersCount = this.correctAnswersCount + 1
      }
    },
    onNextClick () {
      if (this.currentExerciseIndex + 1 === this.exercises.length) {
        this.finished = true
        return
      }

      this.selectedAnswer = null
      this.currentExerciseIndex = this.currentExerciseIndex + 1
    },
    onResetClick () {
      this.currentExerciseIndex = 0
      this.correctAnswersCount = 0
      this.selectedAnswer = null
      this.finished = false
    },
  },
}
</script>

<style lang="scss" scoped>
.lesson-exercises__options {
  margin-top: var(--unit-0400);
}

.lesson-exercises__option {
  cursor: pointer;
  display: flex;
  padding: var(--unit-0200);

  &:not(:last-child) {
    margin-bottom: var(--unit-0400);
  }
}

.lesson-exercises__answers {
  display: flex;
  flex-direction: column;
  margin-top: var(--unit-0800);
}

.lesson-exercises__answer {
  align-items: center;
  background: var(--color-green-000);
  border: var(--border-width-medium) solid var(--color-green-400);
  border-radius: var(--border-radius-medium);
  display: flex;
  padding: var(--unit-0200);
}

.lesson-exercises__answer--correct {
  background: var(--color-green-000);
  border: var(--border-width-medium) solid var(--color-green-400);
  margin-top: var(--unit-0400);

  & .lesson-exercises__answer-letter {
    background: var(--color-green-600);
  }
}

.lesson-exercises__answer--wrong {
  background: var(--color-red-000);
  border: var(--border-width-medium) solid var(--color-red-500);
  margin-bottom: var(--unit-0400);

  & .lesson-exercises__answer-letter {
    background: var(--color-red-600);
  }
}

.lesson-exercises__answer-text {
  flex: 1;
}

.lesson-exercises__answer-letter {
  margin-right: var(--unit-0200);
}

.lesson-exercises__explanation {
  margin-bottom: var(--unit-0400);
  margin-top: var(--unit-0400);
}

.lesson-exercises__cta {
  @media (min-width: $breakpoint-tablet) {
    align-self: flex-end;
  }
}

.lesson-exercises__finished {
  display: flex;
  flex-direction: column;
  gap: var(--unit-0400);
}

.lesson-exercises__illustration {
  display: block;
  margin-bottom: var(--unit-0800);
  margin-left: auto;
  margin-right: auto;
  margin-top: var(--unit-0400);
  max-width: 100%;

  @media (min-width: $breakpoint-tablet) {
    max-width: 60%;
  }
}
</style>
